import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateDefaultMenu, getDefaultMenu, getMenuByUser, MenuDto, MenuFoodDto } from '../services/menus';
import { getAuthUserId } from '../services/apiClient';
import FoodDetailModal from '../components/FoodDetailModal';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import RefreshIcon from '@mui/icons-material/Refresh';
import SpaIcon from '@mui/icons-material/Spa';

// Stable local date key (yyyy-mm-dd) so grouping and weekday calculation use a
// real date rather than a locale-formatted string that Date can't reliably parse.
function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function groupByDay(menuFoods: MenuFoodDto[]) {
  const grouped = new Map<string, MenuFoodDto[]>();
  menuFoods.forEach((item) => {
    const dayKey = toDateKey(new Date(item.day));
    const existing = grouped.get(dayKey) || [];
    existing.push(item);
    grouped.set(dayKey, existing);
  });
  return grouped;
}

export default function Index() {
  const navigate = useNavigate();
  const [menu, setMenu] = useState<MenuDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMenuFood, setSelectedMenuFood] = useState<MenuFoodDto | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const userId = getAuthUserId();

      if (userId) {
        // Logged in user - fetch their menu
        try {
          const userMenu = await getMenuByUser(userId);
          const normalizedMenu = normalizeMenu(userMenu);
          
          // Check if menu configuration is completed
          if (!normalizedMenu?.configuration?.isCompleted) {
            navigate('/menu/configure');
            return;
          }
          
          setMenu(normalizedMenu);
        } catch (err: unknown) {
          const apiError = err as { status?: number };
          
          // Handle 401 - authentication failed
          if (apiError.status === 401) {
            console.error('[Index] Authentication failed - token may be invalid or expired');
            setError('Přihlášení vypršelo. Prosím přihlaste se znovu.');
            setTimeout(() => {
              navigate('/login', { state: { from: { pathname: '/menu' } } });
            }, 2000);
            return;
          }
          
          // If user menu doesn't exist (404), redirect to configure
          if (apiError.status === 404) {
            console.log('[Index] No menu found for user, redirecting to configure');
            navigate('/menu/configure');
            return;
          }
          
          // Other errors - log and redirect to configure
          console.error('Error fetching user menu:', err);
          navigate('/menu/configure');
          return;
        }
      } else {
        // Not logged in - show default menu
        let defaultMenu = await getDefaultMenu();
        let normalized = normalizeMenu(defaultMenu);
        
        if (!normalized || (normalized.foods?.length ?? 0) === 0) {
          const generated = await generateDefaultMenu(7);
          normalized = normalizeMenu(generated);
        }
        
        setMenu(normalized);
      }
    } catch (err) {
      console.error('Error fetching menu:', err);
      setError('Failed to load menu. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRegenerate = useCallback(() => {
    // Refresh menu data after a short delay to allow backend to process
    setTimeout(() => {
      fetchData(false);
    }, 1500);
  }, [fetchData]);

  const normalizeMenu = (menuData: MenuDto | null): MenuDto | null => {
    if (menuData && (menuData as { foods?: unknown }).foods) {
      const foodsValue = (menuData as { foods?: { $values?: MenuFoodDto[] } }).foods;
      const normalizedFoods = Array.isArray(foodsValue)
        ? foodsValue
        : Array.isArray(foodsValue?.$values)
          ? foodsValue?.$values ?? []
          : [];
      return { ...menuData, foods: normalizedFoods };
    }
    return menuData;
  };

  const handleFoodClick = (menuFood: MenuFoodDto) => {
    setSelectedMenuFood(menuFood);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedMenuFood(null);
  };

  // Check if a menu food has discounts
  const hasDiscounts = (menuFood: MenuFoodDto): boolean => {
    return menuFood.discountedProducts && menuFood.discountedProducts.length > 0;
  };

  // Get unique supplier names for discounts (now using names from API)
  const getDiscountSupplierNames = (menuFood: MenuFoodDto): string[] => {
    if (!menuFood.discountedProducts) return [];
    const supplierNames = Array.from(new Set(menuFood.discountedProducts.map(dp => dp.supplierName || dp.supplierId)));
    return supplierNames.filter(name => name);
  };

  // Get total discount price for a menu food
  const getTotalDiscountPrice = (menuFood: MenuFoodDto): number | null => {
    if (!menuFood.discountedProducts || menuFood.discountedProducts.length === 0) return null;
    const prices = menuFood.discountedProducts.filter(dp => dp.price != null).map(dp => dp.price!);
    if (prices.length === 0) return null;
    return prices.reduce((sum, price) => sum + price, 0);
  };

  const menuFoods = useMemo(() => menu?.foods ?? [], [menu?.foods]);
  const groupedFoods = useMemo(() => {
    if (menuFoods.length === 0) {
      return new Map<string, MenuFoodDto[]>();
    }
    return groupByDay(menuFoods);
  }, [menuFoods]);

  const userId = getAuthUserId();
  const isUserMenu = Boolean(userId);

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        gap={2}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Načítám menu...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box m={2}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  if (!menu) {
    return (
      <Box m={2}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>Menu zatím není k dispozici.</Alert>
      </Box>
    );
  }

  // Format day for display
  const formatDay = (dateKey: string) => {
    // dateKey is a local yyyy-mm-dd; build the date explicitly to avoid timezone/parse drift.
    const [year, month, day] = dateKey.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const dayNames = ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'];
    const dayName = dayNames[date.getDay()];
    const displayDate = date.toLocaleDateString('cs-CZ');

    if (date.toDateString() === today.toDateString()) {
      return { day: 'Dnes', date: displayDate, isToday: true };
    }
    if (date.toDateString() === tomorrow.toDateString()) {
      return { day: 'Zítra', date: displayDate, isToday: false };
    }
    return { day: dayName, date: displayDate, isToday: false };
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Hero Section */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 4,
          borderRadius: 3,
          background: (theme) =>
            theme.palette.mode === 'light'
              ? 'linear-gradient(135deg, #ff9800 0%, #e65100 100%)'
              : 'linear-gradient(135deg, #424242 0%, #212121 100%)',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <RestaurantIcon sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          {isUserMenu ? 'Vaše menu' : 'Co budeme jíst?'}
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          {isUserMenu 
            ? 'Personalizované menu podle vašich preferencí'
            : 'Inspirujte se naším výběrem receptů na příští dny'}
        </Typography>
      </Paper>

      {/* Menu Days */}
      {Array.from(groupedFoods.entries()).map(([day, foods]) => {
        const dayInfo = formatDay(day);
        return (
          <Box key={day} sx={{ mb: 5 }}>
            {/* Day Header */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5, 
                mb: 2.5,
                pb: 1,
                borderBottom: 2,
                borderColor: dayInfo.isToday ? 'primary.main' : 'divider',
              }}
            >
              <CalendarTodayIcon 
                color={dayInfo.isToday ? 'primary' : 'action'} 
                fontSize="small"
              />
              <Typography 
                variant="h5" 
                fontWeight={dayInfo.isToday ? 700 : 600}
                color={dayInfo.isToday ? 'primary.main' : 'text.primary'}
              >
                {dayInfo.day}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {dayInfo.date}
              </Typography>
            </Box>

            {/* Food Cards */}
            <Grid container spacing={3}>
              {foods.map((menuFood) => (
                <Grid item xs={12} sm={6} md={4} key={menuFood.id}>
                  {menuFood.requireRegenerate ? (
                    // Show skeleton while regenerating
                    <Card sx={{ height: '100%' }}>
                      <Box sx={{ position: 'relative' }}>
                        <Skeleton variant="rectangular" height={180} animation="wave" />
                        <Box 
                          sx={{ 
                            position: 'absolute', 
                            top: '50%', 
                            left: '50%', 
                            transform: 'translate(-50%, -50%)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <RefreshIcon sx={{ fontSize: 40, color: 'primary.main', animation: 'spin 2s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
                          <Typography variant="body2" color="text.secondary">
                            Generuji nový návrh...
                          </Typography>
                        </Box>
                      </Box>
                      <CardContent>
                        <Skeleton variant="text" width="80%" height={32} />
                        <Skeleton variant="text" width="60%" height={24} />
                      </CardContent>
                    </Card>
                  ) : (
                    <Card 
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 6,
                        },
                      }}
                    >
                      <CardActionArea 
                        onClick={() => handleFoodClick(menuFood)}
                        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                      >
                        {menuFood.food.images && menuFood.food.images.length > 0 ? (
                          <CardMedia
                            component="img"
                            height="180"
                            image={menuFood.food.images[0].path}
                            alt={menuFood.food.name}
                            sx={{ objectFit: 'cover' }}
                          />
                        ) : (
                          <Box 
                            sx={{ 
                              height: 180, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              backgroundColor: 'action.hover',
                            }}
                          >
                            <RestaurantIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                          </Box>
                        )}
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="h6" component="div" fontWeight={600}>
                              {menuFood.food.name}
                            </Typography>
                            {menuFood.food.isVegetarian && (
                              <Chip
                                icon={<SpaIcon />}
                                label="Vegetariánské"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            )}
                          </Box>

                          <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AccessTimeIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {menuFood.food.durationMinutes} min
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PeopleIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {menuFood.food.peopleCount}
                              </Typography>
                            </Box>
                          </Box>

                        {hasDiscounts(menuFood) && (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <LocalOfferIcon fontSize="small" color="success" />
                            {getTotalDiscountPrice(menuFood) != null && (
                              <Chip 
                                label={`${getTotalDiscountPrice(menuFood)!.toFixed(0)} Kč`} 
                                size="small" 
                                color="success" 
                                variant="filled"
                              />
                            )}
                            {getDiscountSupplierNames(menuFood).slice(0, 2).map((name, idx) => (
                              <Chip 
                                key={idx} 
                                label={name} 
                                size="small" 
                                color="success" 
                                variant="outlined"
                              />
                            ))}
                            {getDiscountSupplierNames(menuFood).length > 2 && (
                              <Chip 
                                label={`+${getDiscountSupplierNames(menuFood).length - 2}`} 
                                size="small" 
                                color="success" 
                                variant="outlined"
                              />
                            )}
                          </Box>
                        )}
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}

      {/* Empty State */}
      {menuFoods.length === 0 && (
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
          }}
        >
          <RestaurantIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {isUserMenu 
              ? 'Vaše menu zatím nemá žádná jídla' 
              : 'Základní menu zatím nemá žádná jídla'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isUserMenu && 'Přejděte do konfigurace menu a vygenerujte si návrhy.'}
          </Typography>
        </Paper>
      )}

      <FoodDetailModal
        menuFood={selectedMenuFood}
        open={modalOpen}
        onClose={handleCloseModal}
        menuId={menu?.id}
        onRegenerate={handleRegenerate}
        showRegenerateButton={isUserMenu}
      />
    </Box>
  );
}
